import { Placement } from './types';

/**
 * Returns the border style for the popover based on placement.
 */
export function getPopoverBorder(placement: string, arrowBorderColor: string) {
  switch (placement) {
    case 'right':
      return { border: `1px solid ${arrowBorderColor}`, borderRight: 'none' };
    case 'left':
      return { border: `1px solid ${arrowBorderColor}`, borderLeft: 'none' };
    case 'top':
      return { border: `1px solid ${arrowBorderColor}`, borderTop: 'none' };
    case 'bottom':
      return { border: `1px solid ${arrowBorderColor}`, borderBottom: 'none' };
    default:
      return { border: `1px solid ${arrowBorderColor}` };
  }
}

/**
 * Checks if an element is fully within the viewport.
 */
export function isElementInViewport(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

/**
 * Calculates the popover position based on the target element and placement.
 * Returns null if the target is not found.
 */
export function calculatePosition(target: string, placement: string, offset = 8): { top: number; left: number } | null {
  const anchor = document.getElementById(target);
  if (!anchor) return null;
  const rect = anchor.getBoundingClientRect();
  const scrollY = window.scrollY || document.documentElement.scrollTop;
  const scrollX = window.scrollX || document.documentElement.scrollLeft;

  let top = rect.top + scrollY;
  let left = rect.left + scrollX;

  switch (placement) {
    case 'right':
      top += rect.height / 2;
      left += rect.width + offset;
      break;
    case 'bottom':
      top += rect.height + offset;
      left += rect.width / 2;
      break;
    case 'left':
      top += rect.height / 2;
      left -= offset;
      break;
    case 'top':
    default:
      top -= offset;
      left += rect.width / 2;
      break;
  }

  return { top, left };
}

export function getBestPlacement(target: string, placement: Placement, autoFlip: boolean): Placement {
  if (!autoFlip) return placement;
  const anchor = document.getElementById(target);
  if (!anchor) return placement;
  const rect = anchor.getBoundingClientRect();
  const space = {
    top: rect.top,
    bottom: window.innerHeight - rect.bottom,
    left: rect.left,
    right: window.innerWidth - rect.right,
  };
  // Try preferred, else pick the side with most space
  if (space[placement] > 100) return placement;
  // Find the side with the most space and cast to Placement
  return Object.entries(space).sort((a, b) => b[1] - a[1])[0][0] as Placement;
}
